import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, Download, Share, WifiOff, X } from "lucide-react";
import { Button } from "../ui/button";
import { canPromptInstall, isIos, isStandalone, onInstallChange, promptInstall } from "../../lib/pwa";

const DISMISS_KEY = "va_install_dismissed";

export const useInstallable = () => {
  const [, force] = useState(0);
  useEffect(() => onInstallChange(() => force((n) => n + 1)), []);
  if (isStandalone()) {
    return null;
  }
  return canPromptInstall() ? "prompt" : isIos() ? "ios" : null;
};

export const InstallBanner = () => {
  const mode = useInstallable();
  const { pathname } = useLocation();
  const [hidden, setHidden] = useState(() => localStorage.getItem(DISMISS_KEY) === "1");
  const [iosHelp, setIosHelp] = useState(false);
  if (!mode || hidden || pathname.startsWith("/f/") || pathname.startsWith("/donner-un-avis")) {
    return null;
  }
  const dismiss = () => { localStorage.setItem(DISMISS_KEY, "1"); setHidden(true); };
  const install = async () => { if (mode === "ios") { setIosHelp(true); } else if ((await promptInstall()) === "accepted") { dismiss(); } };
  return (
    <div className="fixed inset-x-3 bottom-3 z-50 rounded-2xl border border-slate-200 bg-white p-4 shadow-xl sm:left-auto sm:right-4 sm:w-96" style={{ marginBottom: "env(safe-area-inset-bottom)" }} data-testid="pwa-install-banner" role="dialog" aria-label="Installer VOXA AFRIKA">
      <div className="flex items-start gap-3">
        <img src="/icons/icon-192.png" alt="" className="h-11 w-11 rounded-xl" />
        <div className="flex-1 text-sm">
          <p className="font-semibold text-slate-900">Installer VOXA AFRIKA</p>
          {!iosHelp ? <p className="text-slate-600">Accès rapide depuis l'écran d'accueil. Facultatif : donner un avis ne nécessite ni installation ni compte.</p>
            : <p className="text-slate-600" data-testid="pwa-ios-help">Dans Safari, touchez <Share className="inline h-4 w-4" /> Partager puis « Sur l'écran d'accueil ».</p>}
        </div>
        <button onClick={dismiss} className="grid h-9 w-9 place-items-center rounded-full text-slate-500 hover:bg-slate-100" aria-label="Fermer" data-testid="pwa-install-dismiss"><X className="h-4 w-4" /></button>
      </div>
      {!iosHelp && <Button onClick={install} className="mt-3 h-11 w-full rounded-full" data-testid="pwa-install-button"><Download className="mr-1.5 h-4 w-4" />Installer</Button>}
    </div>
  );
};

export const InstallMenuButton = () => {
  const mode = useInstallable();
  if (mode !== "prompt") {
    return null;
  }
  return <Button variant="outline" className="rounded-full" onClick={promptInstall} data-testid="pwa-install-menu-button"><Download className="mr-1.5 h-4 w-4" />Installer l'application</Button>;
};

export const OfflineBar = () => {
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);
  if (online) {
    return null;
  }
  return <div className="sticky top-0 z-[60] flex items-center justify-center gap-2 bg-amber-500 px-3 py-1.5 text-sm font-medium text-amber-950" role="status" data-testid="offline-bar"><WifiOff className="h-4 w-4" />Hors connexion — les envois reprendront quand le réseau reviendra.</div>;
};

export const BackButton = ({ home = "/" }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  if (!isStandalone() || pathname === home) {
    return null;
  }
  const back = () => (window.history.state?.idx > 0 ? navigate(-1) : navigate(home));
  return <button onClick={back} className="grid h-10 w-10 place-items-center rounded-full text-slate-700 hover:bg-slate-100" aria-label="Retour" data-testid="pwa-back-button"><ArrowLeft className="h-5 w-5" /></button>;
};
